import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import NavSubmenuTrigger from './navSubmenuTrigger';
import locales from './locales';

const ICON_CARET_UP = 'ri-arrow-up-s-line';
const ICON_CARET_DOWN = 'ri-arrow-down-s-line';

export default class GlobalNav extends Component {
  static propTypes = {
    authInfo: PropTypes.object.isRequired,
    myAccountUrl: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.authInfo = props.authInfo;
    this.handleMenuOption = this.handleMenuOption.bind(this);

    this.initState();
  }

  getSubMenuMarkup(menu) {
    const { lang } = this.props;
    const t = locales(lang);

    return <Fragment key={0}>{t(`globalHeader.submenuItems.${menu}.name`)}</Fragment>;
  }

  getLogoutButton() {
    const { lang, logoutUrl, properties } = this.props;

    const t = locales(lang);

    if (properties && properties.isEmBlue) return [];
    return [
      {
        Component: 'button',
        children: [this.getSubMenuMarkup('logout')],
        to: '#',
        onClick: () => {
          this.authInfo && this.authInfo.clear && this.authInfo.clear();
          window.localStorage.clear();
          window.location = logoutUrl;
        },
        title: t('globalHeader.submenuItems.logout.title'),
      },
    ];
  }

  getCompanyIdForMyAccountUrl() {
    const companyId = this.authInfo.getCompanyId();
    return companyId ? `?company_id=${companyId}` : '';
  }

  getDefaultAccountSubmenu() {
    const { lang, myAccountUrl = '#', properties } = this.props;
    const t = locales(lang);
    if (properties && properties.isEmBlue) return [];

    const account = {
      Component: 'a',
      children: [this.getSubMenuMarkup('account')],
      href: myAccountUrl + this.getCompanyIdForMyAccountUrl(),
      title: t('globalHeader.submenuItems.account.title'),
    };

    return [account];
  }

  getExtraAccountSubmenu() {
    const { extraItems } = this.props;

    return extraItems.map(extraItem => this.getExtraAccountSubmenuItem(extraItem));
  }

  getExtraAccountSubmenuItem(extraItem) {
    const { iconClass, name, onClick, href = '#' } = extraItem;

    const icon = iconClass ? <i className={iconClass} /> : null;

    const submenuItem = {
      Component: 'a',
      children: [
        <Fragment key={0}>
          {icon}
          {name}
        </Fragment>,
      ],
      href,
      title: name,
    };

    if (onClick) {
      submenuItem.onClick = onClick;
    }

    return submenuItem;
  }

  getAttribute(ele, attrib) {
    return ele.dataset[attrib];
  }

  getLanguageButton() {
    const { lang, openLanguageModal } = this.props;
    const t = locales(lang);
    const name = `${lang.toUpperCase()} | ${t(`changeLanguage.${lang}`)}`;
    const languageItemParams = {
      url: '#',
      onClick: openLanguageModal,
      name,
      iconClass: 'ri-global-line',
    };

    return [this.getExtraAccountSubmenuItem(languageItemParams)];
  }

  getAccountSubmenu() {
    const defaultItems = this.getDefaultAccountSubmenu();
    const extraItems = this.getExtraAccountSubmenu();
    const logOutItem = this.getLogoutButton();
    const languageItem = this.getLanguageButton();

    return [...defaultItems, ...extraItems, ...languageItem, ...logOutItem];
  }

  handleMenuOption(e) {
    const currentTarget = e.currentTarget;
    const menu = this.getAttribute(currentTarget, 'id');

    if (this.state[menu]) {
      this.setState({ [menu]: false });
      return true;
    }

    this.resetState();
    this.setState({ [menu]: !this.state[menu] });

    return true;
  }

  resetState() {
    this.initState();
    this.setState(this.state);
  }

  initState() {
    this.state = {
      accountMenu: false,
      helpMenu: false,
      tourMenu: false,
    };
  }

  startTourMenu() {
    const { TourMenu, resumeTour } = this.props;

    this.tourMenu && TourMenu && resumeTour();
  }

  // Warning: react-onclickoutside use this
  handleClickOutside() {
    const { accountMenu, helpMenu } = this.state;

    if (accountMenu || helpMenu) {
      this.resetState();
      return false;
    }

    return true;
  }

  getPrettyUserName(userName) {
    return this.truncateLongName(this.removeDuplicates(userName), 50);
  }

  removeDuplicates(str) {
    return str.replace(/(\b\S.+\b)(?=.*\1)/g, '').trim();
  }

  truncateLongName(str, n) {
    return str.length > n ? str.slice(0, n - 1) + '...' : str;
  }

  getHelpMenuLayer(isVisible) {
    const { showHelpMenu } = this.props;

    if (showHelpMenu) {
      return showHelpMenu(isVisible);
    }
  }

  render() {
    const { lang } = this.props;
    const t = locales(lang);
    const { accountMenu, helpMenu } = this.state;
    const rawUserName = this.authInfo.getUserName();
    const userName = this.getPrettyUserName(rawUserName);

    return (
      <nav className="nav">
        <ul>
          <li data-id="helpMenu" className="nav__menu has-submenu" onClick={this.handleMenuOption}>
            <button
              className={`help__menu--trigger nav__item ${helpMenu ? 'is-active' : ''}`}
              title={t('globalHeader.globalNavItems.help.title')}
            >
              {t('globalHeader.globalNavItems.help.name')}
              <i className="help__menu__icon" />
            </button>
            {this.getHelpMenuLayer(helpMenu)}
          </li>
          <li
            data-id="accountMenu"
            className={`nav__menu has-submenu`}
            onClick={this.handleMenuOption}
          >
            <button
              className={`nav__item ${accountMenu ? 'is-active' : ''}`}
              title={rawUserName}
              type="button"
            >
              {userName}
              {accountMenu ? <i className={ICON_CARET_UP} /> : <i className={ICON_CARET_DOWN} />}
            </button>
            <NavSubmenuTrigger items={this.getAccountSubmenu()} />
          </li>
        </ul>
      </nav>
    );
  }
}

GlobalNav.defaultProps = {
  extraItems: [],
  resumeTour: undefined,
  TourMenu: false,
};

GlobalNav.propTypes = {
  authInfo: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  myAccountUrl: PropTypes.string.isRequired,
  extraItems: PropTypes.array,
  resumeTour: PropTypes.func,
  TourMenu: PropTypes.bool,
  logoutUrl: PropTypes.string,
  openLanguageModal: PropTypes.func,
  showHelpMenu: PropTypes.func,
  properties: PropTypes.shape({
    isEmBlue: PropTypes.bool,
  }),
};
